<template>
  <b-card
    no-body
    class="inspection-category-card mb-0"
    :class="{
      'inspection-category-card--invalid': isCategoryInvalid,
      'inspection-category-card--active': section.isExpanded,
    }"
  >
    <b-row no-gutters class="inspection-category-card__header">
      <div v-if="!viewOnly" class="inspection-category-card__drag-handle">
        <McIconTooltip
          :id="`section-drag-handle-${section.key}`"
          :data-me="`setting-vehicle-inspection-category-drag-${section.key}`"
          :tooltip="$t('vehicleInspection.toolTip.drag')"
          :force-show="isDragging"
          icon="icon-outline_drag-vertical"
          :icon-size="20"
          variant="secondary"
          placement="bottom"
        />
      </div>
      <b-col cols="12" md>
        <b-row no-gutters align-v="center">
          <b-col v-if="isCategoryInvalid" cols="auto" class="mr-2">
            <i
              v-b-tooltip.hover="`${$t('vehicleInspection.toolTip.duplicateWarning')}`"
              class="icon-fill_alert-circle text-danger font-size-20"
            ></i>
          </b-col>
          <b-col>
            <FormulateInput
              v-if="section.isExpanded && !viewOnly"
              id="sectionNameInput"
              ref="sectionNameInput"
              :data-me="`setting-vehicle-inspection-category-input-name-${section.key}`"
              class="inspection-category-card__name"
              :value="section.name"
              :placeholder="$t('vehicleInspection.placeholder.categoryName')"
              maxlength="64"
              underline
              @input="handleNameUpdate"
            />
            <h5 v-else class="inspection-category-card__name">
              {{ section.name }}
            </h5>
          </b-col>
        </b-row>
      </b-col>

      <b-col cols="12" md="auto" class="mt-2 mt-md-0">
        <div class="d-flex align-items-center justify-content-end gap-4">
          <span class="inspection-category-card__count">{{ questionCountLabel }}</span>
          <button
            v-if="!viewOnly"
            v-b-tooltip.hover="`${$t('global.duplicate')} ${$t('global.field.category')}`"
            :data-me="`setting-vehicle-inspection-category-btn-duplicate-${section.key}`"
            type="button"
            class="inspection-category-card__icon-button"
            @click="$emit('duplicate')"
          >
            <i class="mc-icon icon-outline_copy"></i>
          </button>
          <button
            v-if="!viewOnly"
            v-b-tooltip.hover="`${$t('global.delete')} ${$t('global.field.category')}`"
            :data-me="`setting-vehicle-inspection-category-btn-delete-${section.key}`"
            type="button"
            class="inspection-category-card__icon-button inspection-category-card__icon-button--danger ml-2"
            :disabled="!canRemove"
            @click="$emit('remove')"
          >
            <i class="mc-icon icon-outline_trash"></i>
          </button>
          <button
            :data-me="`setting-vehicle-inspection-category-btn-toggle-${section.key}`"
            type="button"
            class="inspection-category-card__toggle ml-2"
            @click="$emit('expand-section')"
          >
            <i
              :class="[
                'mc-icon',
                section.isExpanded ? 'icon-outline_chevron-up' : 'icon-outline_chevron-down',
              ]"
            ></i>
          </button>
        </div>
      </b-col>
    </b-row>

    <hr />

    <div v-show="section.isExpanded" class="inspection-category-card__body">
      <h5 class="inspection-category-card__label" v-text="questionsLabel"></h5>

      <draggable
        :value="questions"
        class="inspection-category-card__questions"
        handle=".inspection-question-item__drag-handle"
        :animation="150"
        :disabled="viewOnly"
        @start="handleQuestionDragStart"
        @input="handleQuestionsUpdate"
        @end="handleQuestionDragEnd"
      >
        <InspectionQuestionItem
          v-for="(question, questionIndex) in questions"
          :key="question.key"
          :question="question"
          :viewOnly="viewOnly"
          :force-show="question.key === draggedQuestionKey"
          :shouldFocusName="question.key === focusQuestionKey"
          :index="questionIndex"
          :canRemove="questions.length > 1"
          :questionInvalid="isQuestionInvalid(questionIndex)"
          @update="handleQuestionUpdate(questionIndex, $event)"
          @delete="$emit('remove-question', questionIndex)"
          @duplicate="$emit('duplicate-question', questionIndex)"
        />
      </draggable>
      <button
        v-if="!viewOnly"
        :data-me="`setting-vehicle-inspection-category-btn-add-question-${section.key}`"
        type="button"
        class="inspection-category-card__add-question"
        @click="$emit('add-question')"
      >
        <span class="me-2">{{ addQuestionLabel }}</span>
        <i class="mc-icon icon-fill_plus"></i>
      </button>
    </div>
  </b-card>
</template>

<script>
import draggable from 'vuedraggable'
import McIconTooltip from '@/components/McIconTooltip'
import InspectionQuestionItem from './InspectionQuestionItem.vue'
import { isInspectionQuestionInvalid, isInspectionSectionInvalid } from '../../lib/utils'

export default {
  components: {
    draggable,
    McIconTooltip,
    InspectionQuestionItem,
  },
  props: {
    section: {
      type: Object,
      required: true,
    },
    sections: {
      type: Array,
      required: true,
    },
    sectionIndex: {
      type: Number,
      required: true,
    },
    shouldFocusName: {
      type: Boolean,
      default: false,
    },
    focusQuestionKey: {
      type: String,
      default: null,
    },
    viewOnly: {
      type: Boolean,
      required: true,
    },
    isDragging: {
      type: Boolean,
      default: false,
    },
    canRemove: {
      type: Boolean,
      required: true,
    },
  },
  emits: [
    'expand-section',
    'update-section',
    'remove',
    'duplicate',
    'add-question',
    'remove-question',
    'duplicate-question',
  ],
  data() {
    return {
      questions: [...this.section.questions],
      draggedQuestionKey: null,
    }
  },
  computed: {
    isCategoryInvalid() {
      return isInspectionSectionInvalid(this.sections, this.sectionIndex)
    },
    questionsCount() {
      return this.questions.length
    },
    questionCountLabel() {
      return `${this.questionsCount} ${this.$t('vehicleInspection.fields.question')}`
    },
    questionsLabel() {
      return this.$t('vehicleInspection.fields.question')
    },
    addQuestionLabel() {
      return this.$t('vehicleInspection.form.addQuestion')
    },
  },
  watch: {
    section: {
      immediate: true,
      deep: true,
      handler(newVal, oldVal) {
        this.questions = [...(newVal.questions || [])]

        if (newVal?.isExpanded && newVal.isExpanded !== oldVal?.isExpanded) {
          this.focusNameInput()
        }
      },
    },
    shouldFocusName(shouldFocusName) {
      if (shouldFocusName) this.focusNameInput()
    },
  },
  methods: {
    focusNameInput() {
      this.$nextTick(() => {
        this.$refs.sectionNameInput?.$el.querySelector('input')?.focus()
      })
    },
    handleNameUpdate(name) {
      this.$emit('update-section', {
        ...this.section,
        name,
      })
    },
    isQuestionInvalid(questionIndex) {
      return isInspectionQuestionInvalid(this.questions, questionIndex)
    },
    handleQuestionDragStart({ oldIndex }) {
      this.draggedQuestionKey = this.questions[oldIndex].key
    },
    handleQuestionDragEnd() {
      this.draggedQuestionKey = null
    },
    handleQuestionsUpdate(questions) {
      this.questions = questions
      this.$emit('update-section', {
        ...this.section,
        questions,
      })
    },
    handleQuestionUpdate(questionIndex, nextQuestion) {
      const nextQuestions = [...this.questions]
      nextQuestions.splice(questionIndex, 1, nextQuestion)
      this.questions = nextQuestions

      this.$emit('update-section', {
        ...this.section,
        questions: nextQuestions,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.inspection-category-card {
  position: relative;
  padding: 40px 12px 12px;
  transition: padding-top 150ms ease;

  &--invalid {
    border: 1px solid $red-300;
  }
  &--active {
    border: 2px solid #a1c5f7;
  }

  &__drag-handle {
    position: absolute;
    top: 12px;
    left: 50%;
    z-index: 1;
    color: #5f6f85;
    width: 40px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transform: translateX(-50%);
    transition: opacity 150ms ease;

    ::v-deep .mc-icon-tooltip {
      margin-right: 0;
    }

    ::v-deep .mc-icon-tooltip__trigger {
      cursor: grab;
    }

    ::v-deep .mc-icon-tooltip__trigger:active {
      cursor: grabbing;
    }
  }

  &__toggle {
    border: none;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #f3f7fd;
    color: #4e6787;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &__name {
    margin-bottom: 0;

    ::v-deep input {
      border: 0;
      border-bottom: 1px solid $mep-primary-200;
      border-radius: 0;
      box-shadow: none;
      font-size: 14px;
      font-weight: $font-weight-bold;
      padding-inline: 0;

      &:focus {
        box-shadow: none;
      }
    }
  }

  &__count {
    background: #eef2f8;
    color: #54657d;
    border-radius: $border-radius-pill;
    padding: 2px 10px;
    font-size: 12px;
    white-space: nowrap;
  }

  &__icon-button {
    border: 0;
    color: #5f6f85;
    background: $white;
    border-radius: 6px;
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--danger {
      color: #e25454;
      background: #ffedeb;
    }
  }

  &__add-question {
    width: 100%;
    border: none;
    margin-top: 12px;
    background: transparent;
    color: #2a5ea7;
    font-weight: $font-weight-bold;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
